import React, { Fragment, useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from "react-helmet-async";

import AppContext from '../../contexts/AppContext'
import Header from '../Header'
import Footer from '../Footer'
import { objToEncoded } from '../../services/search'
import { numberFormat } from '../../services/posts'
import { userNameFrom, avatarFrom } from '../../services/users'

import { Modal } from '../UI'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

import useToastContext from "../../hooks/useToastContext";

const OnboardingBlock = ({ }) => {
    const { data } = useContext(AppContext)
    const user = data?.app?.user

    const businessNotVerified = user?.profile_type == 1 && user?.company && user.company.status != 2
    const userNotVerified = !user?.active

    if(userNotVerified){
        return (
            <div className="alert alert-warning" role="alert">
                <VerifyAccountAction />
            </div>
        )
    }

    if(businessNotVerified){
        return (
            <div className="alert alert-warning" role="alert">
                <Link tag="a" to="/account/business-profile" className="alert-link">Get your business verified</Link> and get a better response to the ad(s) you place.
            </div>
        )
    }

    return null
}

const VerifyAccountAction = ({ }) => {

    const { authServices, data } = useContext(AppContext)
    const addToast = useToastContext()

    const [ loading, setLoading ] = useState()
    const [ wait, setWait ] = useState(false)

    const user = data?.app?.user

    const verify = () => {
        setLoading(true)
        authServices.verifyEmail().then(res => {
            setLoading(false)
            setWait(true)
            addToast({title: 'Success', message: res.message, type: 'success'})
            setTimeout(() => setWait(false), 30000)
        }).catch(err => {
            addToast({title: 'Error', message: err?.data?.message ? err.data.message : 'Something went wrong', type: 'danger'})
            setLoading(false)
            setTimeout(() => setWait(false), 30000)
        })
    }

    if(user?.active) return null

    return (
        <div>
            <span className="me-2">Please Verify your account. </span>
            <button
                className="btn btn-primary btn-sm"
                onClick={verify}
                disabled={loading || wait}
            >
                {loading && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                {!wait && <span>Resend verification email</span>}
                {wait && <span>Verification email has been sent</span>}
            </button>
        </div>
    )
}

const AccountCard = ({ user, dashboard, onChange }) => {

    if(!dashboard) return null
    const { companyServices, setAppState } = useContext(AppContext)
    const userName = userNameFrom(user)
    const avatar = avatarFrom(user)
    const company = user.company
    const addToast = useToastContext()
    const [ submitting, setSubmitting ] = useState(false)
    const canSubmitCompany = company && company.name && company.phone && company.description ? true : false

    const onSubmit = data => {
        return new Promise((resolve, reject) => {
            setSubmitting(true)
            return companyServices.update(company.id, data).then(res => {
                setAppState('app.user.company', res.company)
                setSubmitting(false)
                addToast({title: 'Success', message: 'Save/Update success', type: 'success'})
                resolve(res.company)
            }).catch(err => {
                setSubmitting(false)
                addToast({title: 'Error', message: err?.data?.message ? err.data.message : 'Something went wrong', type: 'danger'})
                reject(err?.data)
            })
        })
    }

    return (
        <div className="card h-100">
            <div className="card-header font-weight-bold d-flex justify-content-between">
                <div><span>Account</span></div>
                <div>
                    {dashboard.personal_score > 0 &&
                        <span
                            className="bg-secondary border badge"
                            style={{cursor: 'default'}}
                            title="Your personal score is an accumulation of affiliate users you've signed up & their active posts, and your active posts."
                        >Personal Score: {dashboard.personal_score}</span>
                    }
                </div>
            </div>
            <div className="card-body d-flex flex-column justify-content-between me-3">
                <div>
                    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                        <div className="card-text mb-3">
                            Name: <a href={user.url} className="me-2">{userName}</a>
                            <span className="badge bg-light text-dark border" style={{cursor: 'default'}} title="Your account rating depends on the number of approved, deleted, saved or flagged posts you have.">
                                Rating: {user.rating}
                            </span>
                            <br />
                            Email: {user.email}<br />
                            Role: <span className="text-capitalize">{user.type}</span><br />
                            {user.default_location && <span>Default location: {user.default_location.name}<br /></span>}

                            <div>
                                <span className="me-2">Account Status:</span>
                                {user.active > 0 && <span className="badge bg-primary">Active</span>}
                                {user.active == 0 && <span className="badge bg-danger">Not verified</span>}
                            </div>

                            {(user.profile_type == 0 || !canSubmitCompany) &&
                                <div>
                                    <Link tag="a" to="/account/business-profile" className="card-link">Business Profile Settings: (fill this out for business verification and get better response to the ad(s) you place)</Link>
                                </div>
                            }
                            {user.profile_type == 1 && canSubmitCompany &&
                                <div>
                                    <Link tag="a" to="/account/business-profile" className="card-link">Business Profile Settings</Link>
                                </div>
                            }

                            {user.profile_type == 1 && user.company &&
                                <div>

                                    <span className="me-2">Business Profile Status:</span>

                                    <span
                                        className={
                                            'me-2 badge badge-' + (
                                                [0, 1].includes(user.company.status) ? 'secondary'
                                                : [2].includes(user.company.status) ? 'success'
                                                : [3, 4].includes(user.company.status) ? 'danger'
                                                : 'secondary'
                                            )
                                        }
                                    >
                                        {user.company.status_text}
                                    </span>

                                    {[0, 4].includes(user.company.status) && canSubmitCompany &&
                                        <button
                                            onClick={() => onSubmit({status: 1})}
                                            className="btn btn-sm btn-success"
                                        >
                                            Submit For Review
                                        </button>
                                    }
                                    {user.company.status != 2 &&
                                        <span className="me-2 badge bg-danger">
                                            Not Verified
                                        </span>
                                    }
                                </div>
                            }
                        </div>
                        <div className="text-center flex-grow-1 flex-md-grow-0 mb-3">
                            <div className="mb-2">
                                <img
                                    className="avatar-img-100"
                                    alt={userName}
                                    title={userName}
                                    src={avatar}
                                />
                            </div>
                            <div>
                                {user.profile_type == 0 &&
                                    <a
                                        href={user.gravatar_url}
                                        target="_blank"
                                        className="btn btn-primary btn-sm"
                                    >Change on Gravatar</a>
                                }
                                {user.profile_type == 1 &&
                                    <Link
                                        tag="a" to="/account/business-logo"
                                        className="btn btn-primary btn-sm"
                                    >Change logo</Link>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <Link tag="a" to="/account/settings" className="card-link">Change Account Settings</Link>
                    <Link tag="a" to="/account/change-password" className="card-link">Change Password</Link>
                </div>
            </div>
        </div>
    )
}

const PostsCard = ({ dashboard }) => {
    return (
        <div className="card h-100">
            <div className="card-header font-weight-bold bg-primary text-white d-flex justify-content-between">
                <div>
                    Posts ads / Banner ads
                </div>
                <div>
                    Any questions? Email us at <a href="mailto:ads@adsitepro.com" target="_blank" className="text-white"><u>ads@adsitepro.com</u></a>
                </div>
            </div>
            <div className="card-body d-flex flex-column justify-content-between">
                <table className="table mb-0">
                    <tbody>
                    <tr>
                            <th>
                                Free postings
                                {dashboard.active_posts_number > 0 && <span> ({dashboard.active_posts_number})</span>}
                            </th>
                            <td>
                                <Link tag="a" to="/account/posts" className="me-3">All your postings</Link>
                                <a href="/create-post" className="me-3 text-success">Create Post</a>
                            </td>
                        </tr>
                        <tr>
                            <th>
                                Bulk postings
                            </th>
                            <td>
                                <a href="/article/bulk-post-ads" className="text-success">Bulk Post Ads</a>
                            </td>
                        </tr>
                        <tr>
                            <th>Banners</th>
                            <td>
                                <Link to="/account/banner/list" tag="a">Your banner ads</Link>
                            </td>
                        </tr>
                        <tr>
                            <th>Text Line Ads</th>
                            <td>
                                <Link to="/account/banner/list" tag="a">Your text line ads</Link>
                            </td>
                        </tr>
                        <tr>
                            <th>
                                Promotions
                                {dashboard.active_promo_number > 0 && <span> ({dashboard.active_promo_number})</span>}
                            </th>
                            <td>
                                <a href="/account/promos">Your promotions ads</a>
                            </td>
                        </tr>
                        <tr>
                            <th>
                                Social media and other platforms of advertising
                            </th>
                            <td>
                                Contact us for specific information about your ad campaign at <a href="mailto:ads@adsitepro.com" target="_blank">ads@adsitepro.com</a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    )
}

const FinanceCard = ({ }) => {

    const { pointServices } = useContext(AppContext)

    const [ loading, setLoading ] = useState(false)
    const [ summary, setSummary ] = useState()

    const loadData = () => {
        pointServices.summary().then(res => {
            setSummary(res.summary)
        }).catch(res => {
            setLoading(false)
            addToast({title: 'Error', message: res?.data?.message ? res.data.message : 'Something went wrong', type: 'danger'})
        })
    }

    useEffect(() => {
        loadData()
    }, [])

    return (
        <div className="card h-100 overflow-hidden">
            <div className="card-header font-weight-bold bg-success text-white">
                Finance
            </div>
            <div className="card-body d-flex flex-column justify-content-between">
                {loading && <div className="text-center"><FontAwesomeIcon icon={faSpinner} spin /></div>}
                {summary &&
                    <div className="mb-3">
                        <div className="d-flex my-1 justify-content-between">
                            <span>Income for your posts</span>
                            <span>{numberFormat(summary.user_post_points / 100)}</span>
                        </div>
                        <div className="d-flex my-1 justify-content-between">
                            <span>Income for your sign ups</span>
                            <span>{numberFormat(summary.affiliate_user_points / 100)}</span>
                        </div>
                        <div className="d-flex my-1 justify-content-between">
                            <span>Income for your sign up's posts</span>
                            <span>{numberFormat(summary.affiliate_post_points / 100)}</span>
                        </div>
                        <div className="d-flex my-1 justify-content-between">
                            <span>Your purchases of credits/points</span>
                            <span>{numberFormat(summary.purchase_points / 100)}</span>
                        </div>
                        <div className="d-flex my-1 justify-content-between">
                            <span>Expenses for banners / text line ads</span>
                            <span>{numberFormat(summary.banner_expense / 100)}</span>
                        </div>
                        <div className="d-flex my-1 justify-content-between">
                            <span>Expenses for posts / links</span>
                            <span>{numberFormat(summary.post_expense / 100)}</span>
                        </div>
                        <div className="d-flex my-1 justify-content-between">
                            <b>Total balance</b>
                            <b>{numberFormat(summary.points / 100)}</b>
                        </div>
                    </div>
                }
                <div>
                    <Link tag="a" to="/account/credits-and-points" className="btn btn-primary d-block d-sm-inline-block mb-2 me-sm-2 mb-sm-0 mb-lg-2">Details</Link>
                    <Link tag="a" to="/account/add-funds" className="btn btn-success d-block d-sm-inline-block mb-2 me-sm-2 mb-sm-0 mb-lg-2">Add Funds to Your Account</Link>
                </div>
            </div>
        </div>
    )
}

const RewardsCard = ({ dashboard, onChange }) => {

    const { data, eventServices } = useContext(AppContext)
    const { app } = data

    const isSignedUp = app && app.user && app.user.contest && app.user.contest.type == 'contest-sign-up'
    const promoUrl = app?.user?.promo_url

    const [ loading, setLoading ] = useState(false)
    const [ showDialog, setShowDialog ] = useState(false)
    const [ dialogOptions, setDialogOptions ] = useState({})

    const contestSignUp = doSignUp => {
        const type = doSignUp ? 'contest-sign-up' : 'contest-sign-out'
        setLoading(true)
        eventServices.fire(app.user.id, 'App\\User', type, true).then(res => {
            setLoading(false)
            if(typeof onChange == 'function') onChange()
        }).catch(res => {
            setLoading(false)
            if(res && res.data && res.data.message) addToast({title: 'Error', message: res.data.message, type: 'danger'})
        })
    }

    const modalDialog = (options) => {
        return new Promise((resolve, reject) => {
            options.onButtonClick = button => {
                if(['cancel', 'close'].includes(button)){
                    setShowDialog(false)
                    reject()
                }
                if(['sign-out'].includes(button)){
                    setShowDialog(false)
                    resolve()
                }
            }
            options.show = showDialog
            setDialogOptions(options)
            setShowDialog(true)
        })
    }

    return (
        <div className="card h-100">
            <div className="card-header font-weight-bold">
                Rewards program!
            </div>
            <div className="card-body d-flex flex-column justify-content-between">
                <div>
                    {!isSignedUp &&
                        <div>
                            <p className="">
                                Sign-Up for the rewards program and share adsitepro.com website with everyone looking to buy, sell or find services and earn credits for your sign ups, their posts & your posts! <a href={process.env.MIX_APP_URL + "/article/affiliate-program"}>Learn more</a>.
                            </p>

                            <button
                                className="btn btn-primary"
                                disabled={loading}
                                onClick={() => contestSignUp(true)}
                            >
                                {loading && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                Sign-Up for rewards program
                            </button>
                        </div>
                    }

                    {isSignedUp &&
                        <>
                            <p>
                                Your promotion link to copy and send to others:<br />
                                <a href={promoUrl} className="font-weight-bold">{promoUrl}</a>
                            </p>
                            <p className="mb-0">
                                Sign-Up for the rewards program and share adsitepro.com website with everyone looking to buy, sell or find services and earn credits for your sign ups, their posts & your posts! <a href={process.env.MIX_APP_URL + "/article/affiliate-program"}>Learn more</a>.
                            </p>
                            {dashboard.activated_total > 0 &&
                                <p className="mb-0 mt-2">
                                    <Link tag="button" className="btn btn-primary btn-block" to="/account/affiliate">See your rewards program numbers</Link>
                                </p>
                            }
                        </>
                    }
                </div>
                <Modal {...dialogOptions} show={showDialog} />
            </div>
        </div>
    )
}

const SalesCard = ({ user }) => {
    return (
        <div className="card h-100 overflow-hidden">
            <div className="card-header font-weight-bold">
                Sales & Leads
            </div>
            <div className="card-body d-flex flex-column justify-content-between">
                {user?.promo_str ? (
                    <div className="mb-2">
                        Promo Code: <b>{user.promo_str}</b>
                    </div>
                ) : (
                    <div className="mb-2">
                        Promo code is not set. Please contact administrator.
                    </div>
                )}

                <div className="mb-2">
                    <a href={`/api/user/${user.id}/qr-code`} target="_blank">
                        <img src={`/api/user/${user.id}/qr-code`} alt="" className="img-fluid my-3" />
                    </a>
                </div>

                <div>
                    <Link tag="a" to="/account/leads" className="btn btn-primary me-2">Leads</Link>
                    <Link tag="a" to="/account/leads/create" className="btn btn-success">Create lead</Link>
                </div>
            </div>
        </div>
    )
}

const Home = ({ match, history }) => {

    const { data, setAppState, authServices, eventServices, location } = useContext(AppContext)
    const { app, isReady } = data
    const { user } = app

    const user_str_id = app && app.user && app.user.str_id ? app.user.str_id : app.user_str_id

    const [ loading, setLoading ] = useState(false)
    const [ dashboard, setDashboard ] = useState({})

    const addToast = useToastContext()

    const userListUrl = type => {
        var data = {user_str_id: user_str_id}
        if(type) data.list = type
        return user_str_id ? ('/search#' + objToEncoded(data)) : ''
    }

    const loadDashboard = () => {
        setLoading(true)
        authServices.user().then(data => {
            setDashboard(data.affiliatesData)
            setAppState('app.user', data.user)
            setLoading(false)
        }).catch(res => {
            addToast({title: 'Error', message: res?.data?.message ? res.data.message : 'Something went wrong', type: 'danger'})
            setLoading(false)
        })
    }

    useEffect(() => {
        if(isReady) loadDashboard()
    }, [isReady])

    if(!user) return null

    return (
        <Fragment>
            <div>
                <Header history={history} />
                <main className="py-4">
                    <div className="container">
                        <Helmet><title>{'Dashboard | ' + process.env.MIX_APP_NAME}</title></Helmet>

                        <h1>Dashboard</h1>

                        <OnboardingBlock />

                        <div className="row row-eq-height">

                            <div className="col-xl-6 col-lg-6 mb-4">
                                <AccountCard
                                    user={user}
                                    dashboard={dashboard}
                                    onChange={() => loadDashboard()}
                                />
                            </div>

                            <div className="col-xl-6 col-lg-6 mb-4">
                                <PostsCard dashboard={dashboard} />
                            </div>

                            <div className="col-xl-4 col-lg-6 mb-4">
                                <FinanceCard />
                            </div>

                            {app?.user?.promo_url &&
                                <div className="col-xl-4 col-lg-6 mb-4">
                                    <RewardsCard dashboard={dashboard} onChange={loadDashboard} />
                                </div>
                            }

                            {authServices.isSales() &&
                                <div className="col-xl-4 col-lg-6 mb-4">
                                    <SalesCard user={user} />
                                </div>
                            }

                        </div>
                    </div>
                </main>
            </div>
            <Footer />
        </Fragment>
    )
}

export default Home
